import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem
} from "reactstrap";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import PaymentReportTable from "../../../components/table/all-tables/reportable/paymenttable/PaymentReportTable";
import {
  formatDateWithDayjs,
  get14DaysAgoDate,
  showAlert,
  showAlertWithHtml,
  showToast,
  showSettledInXeroNotice,
  settledInXeroWarningOf
} from "../../../utils/utils";
import {
  alertActions,
  commonErrMsg,
  dashboard,
  invalidToastContent,
  passwordConfirmModal,
  paymentArchivedAction,
  paymentReport,
  responseStatus,
  successToastContent,
  UserManagment,
  userStatusAction
} from "../../../locales/en";
import {
  getUserRepaymentsReport,
  getManualPaymentSync,
  checkXeroSyncStatus
} from "../../../redux/userReportsSlice";
import { useDispatch, useSelector } from "react-redux";
import PaymentHeader from "./PaymentHeader";
import TableSkeleton from "../../../components/skeletons/TableSkeleton";
import {
  CURRENT,
  PAGE_INDEX,
  REPORT_PAGE_SIZE,
  showToastType,
  STATUS_5,
  SUBMITTED_DIRECT_DEBIT,
  SYNC_POLLING_INTERVAL
} from "../../../utils/constants/appConstants";
import { PAYMENT_STATUS_MAPPING } from "../../../utils/getDropdownOptions";
import PaymentFilterShow from "./PaymentFilterShow";
import { useNavigate } from "react-router-dom";
import CustomerStatusUpdateInfo from "../../../components/modals/CustomerStatusUpdateInfo";
import CustomerStatusBulkUpdateApprovalInfo from "../../../components/modals/CustomerStatusBulkUpdateApprovalInfo";
import PasswordSetModal from "../../../components/modals/PasswordSetModal";
import {
  getCustomerInvoiceStatusApprovalData,
  setApprovedStatusInfo
} from "../../../redux/invoiceDetailsSlice";
import privateAxios from "../../../utils/axiosPrivate";
import {
  getArchivePaymentReportData,
  getBulkUpdateQuotesStatusUrl,
  getPasswordConfirmationURL,
  getPostDateUrl,
  getPutDateUrl
} from "../../../utils/constants/apiEndPoints";
import useMixpanelEventAction from "../../../config/mixpanel/useMixpanelEventAction";
import "../reportStyle.scss";

const CurrentPaymentsReport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    quoteIds,
    paymentIds,
    selectedUserAccountIds,
    userQuoteIdsSelected,
    showTopHideFilter
  } = useSelector((state) => state.userReport || {});
  const { rejectInvoiceMixpanelEvent } = useMixpanelEventAction();
  const profile = useSelector((state) => state.userManagement?.profile);
  const titleFields = ["from", "to", "invoice_statuses"];
  const [initialStartDate, setInitialStartDate] = useState(get14DaysAgoDate());
  const [initialEndDate, setInitialEndDate] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(initialStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState(initialEndDate);
  const [selectedStatus, setSelectedStatus] = useState(CURRENT);
  const [filterTrigger, setFilterTrigger] = useState(0);
  const [allReportsData, setAllReportsData] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [pageIndexMap, setPageIndexMap] = useState({});
  const [pageSizeMap, setPageSizeMap] = useState({});
  const [isXeroSyncing, setIsXeroSyncing] = useState(false);
  const xeroPollingRef = useRef(null);
  const { loading: TableDataLoading } = useSelector(
    (state) => state.userReport
  );
  const isInitialSearch = useRef(true);
  const initializedTablesRef = useRef({});

  const handleStartDateChange = (date) => {
    setSelectedStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setSelectedEndDate(date);
  };

  const [open, setOpen] = useState([]);

  const toggle = (id) => {
    setOpen((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handlePaymentsReportData = async (
    startDate,
    endDate,
    status,
    specificKey = null,
    page,
    size
  ) => {
    const keys = Object.keys(PAYMENT_STATUS_MAPPING || {});
    const statusKeys = keys.filter((k) => !titleFields.includes(k));

    const keysToFetch = specificKey
      ? [specificKey]
      : statusKeys.map((k) => PAYMENT_STATUS_MAPPING[k]);

    // Set loading state for specific keys
    setAllReportsData((prev) => {
      const updated = { ...prev };
      keysToFetch.forEach((k) => {
        updated[k] = { ...updated[k], loading: true };
      });
      return updated;
    });

    if (!specificKey) setBulkLoading(true);

    try {
      const promises = keysToFetch.map(async (paymentKey) => {
        const paramsData = {
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          pageType: status,
          paymentStatus: paymentKey,
          page: page,
          size: size
        };
        const result = await dispatch(
          getUserRepaymentsReport(paramsData)
        ).unwrap();

        const resData = result?.data?.data || result?.data || result || [];
        const actualData = Array.isArray(resData)
          ? resData
          : resData?.data || [];

        return {
          key: paymentKey,
          data: actualData,
          totalPages:
            result?.pageInfo?.totalPages ||
            result?.data?.pageInfo?.totalPages ||
            PAGE_INDEX,
          pageIndex: page,
          pageSize: size
        };
      });

      const allResults = await Promise.all(promises);
      setAllReportsData((prev) => {
        const newData = { ...prev };
        allResults.forEach((res) => {
          newData[res.key] = {
            data: res.data,
            totalPages: res.totalPages,
            loading: false
          };
        });
        return newData;
      });

      if (!specificKey && isInitialSearch.current) {
        const keys = Object.keys(PAYMENT_STATUS_MAPPING || {});
        const newOpen = [];

        allResults.forEach((res) => {
          if (res.data?.length > 0) {
            const keyName = Object.keys(PAYMENT_STATUS_MAPPING).find(
              (k) => PAYMENT_STATUS_MAPPING[k] === res.key
            );

            if (keyName === SUBMITTED_DIRECT_DEBIT) {
              return;
            }

            const idx = keys.indexOf(keyName);

            if (idx !== -1) {
              newOpen.push(String(idx));
            }
          }
        });

        setOpen(newOpen);
        isInitialSearch.current = false;
      }

      return allResults;
    } catch (error) {
      showToast(
        error?.data?.message || commonErrMsg.item2,
        showToastType.error
      );
      return null;
    } finally {
      setBulkLoading(false);
      setAllReportsData((prev) => {
        const updated = { ...prev };
        keysToFetch.forEach((k) => {
          if (updated[k]) updated[k].loading = false;
        });
        return updated;
      });
    }
  };

  useEffect(() => {
    handlePaymentsReportData(
      initialStartDate,
      initialEndDate,
      selectedStatus,
      null,
      pageIndexMap[CURRENT] || PAGE_INDEX,
      pageSizeMap[CURRENT] || REPORT_PAGE_SIZE
    );
  }, [initialStartDate, initialEndDate, selectedStatus, filterTrigger]);

  const handleFilterSubmit = () => {
    isInitialSearch.current = true;
    setInitialStartDate(selectedStartDate);
    setInitialEndDate(selectedEndDate);
    setFilterTrigger((prev) => prev + 1);
  };

  // Check Xero sync status
  const checkXeroStatus = async () => {
    try {
      const result = await dispatch(checkXeroSyncStatus()).unwrap();
      const isInProgress = result?.data?.data || false;
      setIsXeroSyncing(isInProgress);
      return isInProgress;
    } catch {
      return false;
    }
  };

  // Start polling for Xero sync status
  const startXeroPolling = () => {
    if (xeroPollingRef.current) {
      clearTimeout(xeroPollingRef.current);
    }

    const poll = async () => {
      try {
        const isInProgress = await checkXeroStatus();
        if (isInProgress) {
          // Schedule next poll only after this one completes
          xeroPollingRef.current = setTimeout(poll, SYNC_POLLING_INTERVAL); // 20 seconds
        } else {
          // Sync completed, stop polling
          xeroPollingRef.current = null;
        }
      } catch {
        // Handle error and continue polling
        xeroPollingRef.current = setTimeout(poll, SYNC_POLLING_INTERVAL); // Retry after 20 seconds
      }
    };

    poll(); // Start immediately
  };

  // Stop polling
  const stopXeroPolling = () => {
    if (xeroPollingRef.current) {
      clearTimeout(xeroPollingRef.current);
      xeroPollingRef.current = null;
    }
  };

  // Handle Xero sync
  const handleSyncXero = async () => {
    try {
      const email = profile?.primaryEmail;
      await dispatch(getManualPaymentSync({ email })).unwrap();
      setIsXeroSyncing(true);
      startXeroPolling();
    } catch (error) {
      showToast(error?.message || commonErrMsg.item2, showToastType.error);
    }
  };

  useEffect(() => {
    checkXeroStatus();
    return () => {
      stopXeroPolling();
    };
  }, []);

  const refreshCurrentPaymentData = async () => {
    const keys = Object.keys(PAYMENT_STATUS_MAPPING || {});
    const statusKeys = keys.filter((k) => !titleFields.includes(k));

    const promises = statusKeys.map((k) => {
      const paymentKey = PAYMENT_STATUS_MAPPING[k];
      const size = pageSizeMap[paymentKey] || REPORT_PAGE_SIZE;

      return handlePaymentsReportData(
        initialStartDate,
        initialEndDate,
        selectedStatus,
        paymentKey,
        PAGE_INDEX,
        size
      );
    });

    await Promise.all(promises);
  };

  // Group Status Change Function Start
  const [grpStatus, setGrpStatus] = useState("");
  const [grpRepaydate, setGrpRepaydate] = useState("");
  const [addPasswordModal, setAddPasswordModal] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const prevGrpStatusRef = useRef(grpStatus);
  const [updateApprovalinfo, setUpdateApprovalinfo] = useState(false);
  const [statusNewValue, setStatusNewValue] = useState(null);
  const [password, setPassword] = useState("");
  const [approvalData, setApprovalData] = useState({});
  const { approvedStatusInfo, customerStatusData } = useSelector(
    (state) => state.invoiceDetails
  );

  const statusInfoData = customerStatusData || [];

  const handleToggleStatusInfo = () => {
    dispatch(setApprovedStatusInfo(false));
    setPassword("");
  };

  const handleApprovalStatusUpdate = () => {
    setUpdateApprovalinfo(!updateApprovalinfo);
  };

  const handleModalClose = () => {
    setAddPasswordModal(false);
  };

  const handleGroupStatusChange = async (event) => {
    const { value } = event.target;
    setStatusNewValue(value);

    try {
      const result = await showAlert(
        userStatusAction?.title,
        userStatusAction?.subH2,
        alertActions?.warning,
        alertActions?.Confirm,
        alertActions?.Cancel
      );
      if (result?.isDismissed) {
        setGrpStatus("");
        navigate;
        return;
      }

      if (!result.isConfirmed) {
        event.target.value = prevGrpStatusRef.current;
        return;
      }
      await proceedToUpdate(value);
    } catch (err) {
      showToast(err?.response?.data?.message, responseStatus?.error);
      event.target.value = prevGrpStatusRef.current;
    }
  };

  const proceedToUpdate = async (statusValue) => {
    const dataToSend = {
      quoteIds,
      statusId: statusValue
    };

    const response = await privateAxios.put(
      getBulkUpdateQuotesStatusUrl,
      dataToSend
    );

    if (response.status === 200) {
      const resData = response?.data?.data;
      if (statusValue === STATUS_5 && resData?.rejectedQuoteList?.length) {
        rejectInvoiceMixpanelEvent(resData.rejectedQuoteList);
      }
      refreshCurrentPaymentData();
      showToast(successToastContent?.message, successToastContent?.status);
      setGrpStatus("");
    }
  };

  const validatePassword = async (password) => {
    try {
      const dataToSend = { password };
      const response = await privateAxios.post(
        getPasswordConfirmationURL,
        dataToSend
      );
      return response.status === 200;
    } catch (err) {
      showToast(
        err?.response?.data?.message || err?.response?.data?.errors,
        responseStatus?.error
      );
      return false;
    }
  };

  const handlePasswordSubmit = async () => {
    const isValid = await validatePassword(password);
    if (!isValid) {
      showToast(passwordConfirmModal?.validationMessage, showToastType?.error);
      return;
    }

    dispatch(setApprovedStatusInfo(false));
    setPassword("");

    if (statusNewValue !== null) {
      handleUpdateApprovalStatus();
    }
  };

  const handleUpdateApprovalStatus = async (updateValue) => {
    try {
      const payload = {
        quoteIds: userQuoteIdsSelected
      };
      const response = await dispatch(
        getCustomerInvoiceStatusApprovalData(payload)
      ).unwrap();
      const responseData = response.data.data;
      if (response.status === 200 && responseData?.status === false) {
        await proceedToUpdate(statusNewValue || updateValue);
      } else if (response.status === 200 && responseData?.status === true) {
        setApprovalData(response.data.data);
        setUpdateApprovalinfo(true);
      }
    } catch {
      // handle catch here;
    }
  };

  const handleGroupRepayDate = async (date) => {
    const formattedDate = date ? date.toLocaleDateString("en-CA") : "";
    setGrpRepaydate(formattedDate);

    try {
      const repaymentDate = formattedDate;

      setPendingUpdate({ repaymentDate, quoteIds });

      const dataToSendPost = {
        quoteIds: quoteIds,
        repaymentDate: repaymentDate
      };

      const postresponse = await privateAxios.post(
        getPostDateUrl,
        dataToSendPost
      );
      const responseData = postresponse.data?.data;
      const eligibleQuoteIds = responseData?.eligibleQuoteIds || [];
      const notEligibleQuoteIds = responseData?.notEligibleQuoteIds || [];
      const quoteDetails = responseData?.quoteDetails || [];
      const hasMoreThan30Days = quoteDetails.some((item) => item.dayCount > 30);

      // Extract messages for eligible quotes
      const eligibleMessages = quoteDetails
        .map(
          (item) => `
                      <li>
                          <p><strong>FT${item.quoteId}</strong> - ${item.message}</p>
                          ${item.dayCount > 30 ? '<p class="info"><span>🛈</span> The repayment date is delayed by more than 30 days from the due date.</p>' : ""}
                      </li>
                  `
        )
        .join("");

      // Extract messages for not eligible quotes
      const notEligibleMessages = notEligibleQuoteIds
        .map(
          (id) => `
                      <li class="not-eligible">
                          <p><strong>FT${id}</strong> - Invoices are not eligible to change repay date</p>
                      </li>
                  `
        )
        .join("");

      const messageHtml = `
                  <ul class="repayBorder">
                      ${eligibleMessages}
                      ${notEligibleMessages}
                  </ul>
              `;

      const result = await showAlertWithHtml(
        dashboard?.repayDateAlert?.title,
        messageHtml,
        "",
        alertActions?.Confirm,
        alertActions?.Cancel,
        dashboard?.repayDateAlert?.alertSize
      );

      if (result.isConfirmed) {
        if (notEligibleQuoteIds.length > 0) {
          return;
        }
        const dataToSend = {
          quoteIds: eligibleQuoteIds,
          repaymentDate: repaymentDate
        };

        if (hasMoreThan30Days) {
          setPendingUpdate({ repaymentDate, quoteIds });
          setAddPasswordModal(true);
        } else {
          const response = await privateAxios.put(getPutDateUrl, dataToSend, {
            params: { checkEligibility: true }
          });
          if (response.status === 200) {
            showToast(
              successToastContent?.message,
              successToastContent?.status
            );
            const settledWarning = settledInXeroWarningOf(response);
            if (settledWarning) {
              showSettledInXeroNotice(settledWarning);
            }
            refreshCurrentPaymentData();
          }
        }
      }
    } catch {
      showToast(invalidToastContent?.message, invalidToastContent?.status);
    }
  };

  const saveRepayDate = async (repaymentDate, quoteIds) => {
    try {
      const dataToSendPost = {
        quoteIds: quoteIds,
        repaymentDate: repaymentDate
      };

      const response = await privateAxios.put(getPutDateUrl, dataToSendPost, {
        params: { checkEligibility: true }
      });

      if (response.status === 200) {
        showToast(successToastContent?.message, successToastContent?.status);
        const settledWarning = settledInXeroWarningOf(response);
        if (settledWarning) {
          showSettledInXeroNotice(settledWarning);
        }
      }
    } catch (error) {
      showToast(error.response?.data?.message, responseStatus?.error);
    }
  };

  const handlePasswordEntered = async (password) => {
    const isValid = await validatePassword(password);
    if (!isValid) return;

    setAddPasswordModal(false);

    if (pendingUpdate) {
      await saveRepayDate(pendingUpdate.repaymentDate, pendingUpdate.quoteIds);
      setPendingUpdate(null);
      refreshCurrentPaymentData();
    }
  };

  const handleArchiveInvoices = async () => {
    const result = await showAlert(
      paymentArchivedAction?.title,
      paymentArchivedAction?.subH2,
      alertActions?.warning,
      alertActions?.Confirm,
      alertActions?.Cancel
    );
    if (result?.isDismissed) {
      return;
    }
    try {
      setArchivedLoading(true);
      const response = await privateAxios.put(
        getArchivePaymentReportData,
        paymentIds
      );
      showToast(response?.data?.message, responseStatus?.success);
      refreshCurrentPaymentData();
    } catch (error) {
      showToast(error?.response?.data?.message, responseStatus?.error);
    } finally {
      setArchivedLoading(false);
    }
  };

  const invoiceFilterProps = {
    handleGroupStatusChange,
    handleGroupRepayDate,
    handleArchiveInvoices,
    grpStatus,
    grpRepaydate,
    selectedUserAccountIds,
    archivedLoading
  };

  const handlePaginationChange = (paymentKey, newPageIndex, newPageSize) => {
    const currentPageIndex = pageIndexMap[paymentKey] ?? PAGE_INDEX;
    const currentPageSize = pageSizeMap[paymentKey] ?? REPORT_PAGE_SIZE;

    if (
      initializedTablesRef.current[paymentKey] &&
      newPageIndex === currentPageIndex &&
      newPageSize === currentPageSize
    ) {
      return;
    }

    initializedTablesRef.current[paymentKey] = true;

    setPageIndexMap((prev) => ({
      ...prev,
      [paymentKey]: newPageIndex
    }));

    setPageSizeMap((prev) => ({
      ...prev,
      [paymentKey]: newPageSize
    }));

    handlePaymentsReportData(
      initialStartDate,
      initialEndDate,
      selectedStatus,
      paymentKey,
      newPageIndex,
      newPageSize
    );
  };

  return (
    <>
      <TablePagesTitle mainTitle={paymentReport?.mainTitle} />
      <div className="row mb-2 mx-0">
        <div className="col-12 sales-page-title-wrap sub-heading-blog-wrap pb-2">
          <div className="row box-heading">
            <div className="col-md-12">
              <h2>
                {`${`${paymentReport?.subH2} `}
                  ${formatDateWithDayjs(
                    initialStartDate.toISOString(),
                    "DD-MMM-YYYY"
                  )}
                  -
                  ${` ${formatDateWithDayjs(initialEndDate.toISOString(), "DD-MMM-YYYY")}`}
                `}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {showTopHideFilter && (
        <div className="row mb-3 mt-3 mx-0">
          <div className="col-12 filter-wrap pb-2 ps-0 pe-0">
            <PaymentFilterShow {...invoiceFilterProps} />
          </div>
        </div>
      )}
      <div className="row mb-3 mt-3 mx-0">
        <div className="col-12 filter-wrap pb-2 ps-0 pe-0">
          <PaymentHeader
            setSelectedStatus={setSelectedStatus}
            handleEndDateChange={handleEndDateChange}
            handleStartDateChange={handleStartDateChange}
            initialStartDate={selectedStartDate}
            initialEndDate={selectedEndDate}
            selectedStatus={selectedStatus}
            loading={bulkLoading || TableDataLoading}
            onFilter={handleFilterSubmit}
            isXeroSyncing={isXeroSyncing}
            handleSyncXero={handleSyncXero}
          />
        </div>
      </div>
      <Accordion open={open} toggle={toggle} className="custom-accordion">
        {Object.keys(PAYMENT_STATUS_MAPPING || {}).map((key, index) => {
          if (titleFields.includes(key)) return null;
          return (
            <AccordionItem key={index} className="mb-2">
              <AccordionHeader targetId={String(index)}>{key}</AccordionHeader>
              <AccordionBody accordionId={String(index)}>
                <div className="table-blog-title mt-2 mb-2">
                  <h2>{key}</h2>
                </div>
                {allReportsData[PAYMENT_STATUS_MAPPING[key]]?.loading &&
                open.includes(String(index)) ? (
                  <div className="table-wrapper">
                    <table className="mt-2 mb-3 paymentreport-table-wrap bgless_table w-100">
                      <thead>
                        <tr>
                          {Array.from({ length: 12 }).map((_, i) => (
                            <th key={i}>
                              <div className="h-20" />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <TableSkeleton rows={10} columns={12} height={20} />
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <PaymentReportTable
                    data={
                      open.includes(String(index))
                        ? allReportsData[PAYMENT_STATUS_MAPPING[key]]?.data ||
                          []
                        : []
                    }
                    loading={
                      allReportsData[PAYMENT_STATUS_MAPPING[key]]?.loading
                    }
                    totalPages={
                      allReportsData[PAYMENT_STATUS_MAPPING[key]]?.totalPages ||
                      0
                    }
                    pageIndex={pageIndexMap[PAYMENT_STATUS_MAPPING[key]] || 0}
                    pageSize={
                      pageSizeMap[PAYMENT_STATUS_MAPPING[key]] ||
                      REPORT_PAGE_SIZE
                    }
                    setPageIndex={(newPageIndex) =>
                      handlePaginationChange(
                        PAYMENT_STATUS_MAPPING[key],
                        newPageIndex,
                        pageSizeMap[PAYMENT_STATUS_MAPPING[key]] ||
                          REPORT_PAGE_SIZE
                      )
                    }
                    setPageSize={(newPageSize) =>
                      handlePaginationChange(
                        PAYMENT_STATUS_MAPPING[key],
                        0,
                        newPageSize
                      )
                    }
                  />
                )}
              </AccordionBody>
            </AccordionItem>
          );
        })}
      </Accordion>

      {approvedStatusInfo && (
        <CustomerStatusUpdateInfo
          isOpen={approvedStatusInfo}
          toggle={handleToggleStatusInfo}
          errorData={statusInfoData}
          content={
            UserManagment?.financialsTab?.aiCreditScoreSection?.passwordModal
              ?.subTitle
          }
          onSubmit={handlePasswordSubmit}
          password={password}
          setPassword={setPassword}
        />
      )}
      <PasswordSetModal
        isOpen={addPasswordModal}
        toggle={handleModalClose}
        handlePasswordEntered={handlePasswordEntered}
      />
      <CustomerStatusBulkUpdateApprovalInfo
        isOpen={updateApprovalinfo}
        toggle={handleApprovalStatusUpdate}
        userQuoteIdsSelected={userQuoteIdsSelected}
        approvalData={approvalData}
        handleGroupStatusUpdate={proceedToUpdate}
        statusNewValue={statusNewValue}
      />
    </>
  );
};

export default CurrentPaymentsReport;
